import { fromAbsolute, toCalendarDate } from '@internationalized/date';
import {
  getPresetRange,
  type PlumaDatePickerPreset,
  type PlumaDatePickerPresetClickOptions,
} from '@customerio/pluma-components/react/utils/date-picker';
import { formatCentsToUsd } from '@/utils/currency';
import type { ActivityHistory } from './activity-history';

const LOCKED_PRESET_TITLE = 'Data retention add-on';
const LOCKED_PRESET_REMEDY = 'Nothing is deleted, add it and this range reappears.';

/** A user who can edit billing sees the price. Other users are told to ask an admin. */
export function lockedPresetTooltip(priceCents: number | null, canEditBilling: boolean): string {
  if (!canEditBilling) {
    return `${LOCKED_PRESET_TITLE}: ask an admin to add it. Nothing is deleted, and this range reappears.`;
  }
  if (priceCents == null) {
    return `${LOCKED_PRESET_TITLE}: nothing is deleted, add it and this range reappears.`;
  }
  return `${LOCKED_PRESET_TITLE}: ${formatCentsToUsd(priceCents, 0)} per billing cycle for the whole account. ${LOCKED_PRESET_REMEDY}`;
}

export type ActivityHistoryPresets = {
  presets: PlumaDatePickerPreset[];
  isPresetDisabled?: (preset: PlumaDatePickerPreset) => boolean;
};

type LookbackStarts = Pick<ActivityHistory, 'lookbackStart' | 'addonLookbackStart'>;

export type PresetLock = {
  tooltip: string;
  /** Opens the add-on. When it is absent, a locked preset is disabled. */
  onUnlock?: () => void;
};

/**
 * Locks the presets that reach past the included window. A preset the add-on would unlock gets a
 * lock icon and the tooltip. A preset that reaches past the add-on window too is disabled.
 */
export function lockPresetsBefore(
  presets: PlumaDatePickerPreset[],
  { lookbackStart, addonLookbackStart }: LookbackStarts,
  localTimeZone: string,
  { tooltip, onUnlock }: PresetLock,
): ActivityHistoryPresets {
  const toDay = (unixSeconds: number) => toCalendarDate(fromAbsolute(unixSeconds * 1000, localTimeZone));
  const firstVisibleDay = toDay(lookbackStart);
  const firstAddonDay = toDay(addonLookbackStart);
  const disabled = new Set<PlumaDatePickerPreset>();
  const lockedPresets = presets.map((preset) => {
    const start = getPresetRange(preset, { localTimeZone }).start;
    if (start.compare(firstVisibleDay) >= 0) return preset;

    if (start.compare(firstAddonDay) < 0) {
      disabled.add(preset);
      return preset;
    }

    const lockedPreset: PlumaDatePickerPreset = { ...preset, icon: 'lock-secure', tooltip };
    if (onUnlock == null) {
      disabled.add(lockedPreset);
      return lockedPreset;
    }
    const unlockablePreset: PlumaDatePickerPreset = {
      ...lockedPreset,
      onClick: ({ close }: PlumaDatePickerPresetClickOptions) => {
        close();
        onUnlock();
      },
    };
    return unlockablePreset;
  });

  return { presets: lockedPresets, isPresetDisabled: (preset) => disabled.has(preset) };
}
